// Admin-wide activations table (ATAD-132): what data went to which
// destination, for every user.
//
// Sibling of OrdersTable.tsx, which it deliberately mirrors — same react-table
// setup, same header chrome, same `setAllFilters` handle so the page's Clear
// Filters button works against whichever table is mounted. The ~50 lines of
// table markup are duplicated rather than extracted: a shared <AdminTable>
// would need render-props for the header cell and the row-count line, and at
// two consumers the indirection costs more than it saves.
//
// One deliberate difference from OrdersTable: dates and numbers use a raw
// accessor plus a Cell formatter, so react-table's comparator sees numbers.
// OrdersTable formats inside the accessor, which makes its date and number
// columns sort as text ("Sep 9" after "Sep 10"). Don't copy that here.

import { useMemo, forwardRef, useImperativeHandle, Fragment } from 'react';
import { useTable, useSortBy, useFilters, useExpanded } from 'react-table';
import { FaArrowDown, FaArrowUp, FaSort } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { MdOutlineErrorOutline } from 'react-icons/md';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';

import {
  formatDateCellCustom,
  formatNumberWithCommas,
} from '../../helpers/reactTable';
import { MultiSelectColumnFilter, multiSelectFilter } from './tableFilters';
import { extractVariablesFromAudienceFilters } from '../../helpers/audienceFilters';

const EMPTY = '—';

/** Epoch-ms strings arrive from GraphQL as `String`; sort them as numbers. */
const toEpoch = (value) => (value ? Number(value) : null);

/**
 * The audience name to show. The activation's own copy is preferred over the
 * live audience document: it records what was actually sent, and it survives
 * the audience being renamed or deleted.
 */
export const activationAudienceName = (row) =>
  row.audienceName || row.audience?.name || EMPTY;

/**
 * The destination brand. Activations written by the retired checkout flow have
 * no destinationPartner ref, but they do carry the denormalized brand name.
 */
export const activationDestination = (row) =>
  row.destinationPartner?.name || row.destinationPartnerName || EMPTY;

function StatusCell({ value }) {
  if (value === 'pending') {
    return (
      <span className="flex items-center gap-1">
        <CgSpinner className="text-xl text-blue animate-spin" />
        <span>Processing</span>
      </span>
    );
  }

  if (value === 'failed') {
    return (
      <span className="flex items-center gap-1">
        <MdOutlineErrorOutline className="text-xl text-orange" />
        <span>Failed</span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1">
      <IoIosCheckmarkCircleOutline className="text-xl text-green-light" />
      <span>Completed</span>
    </span>
  );
}

/**
 * The drill-in. There is no admin-side audience page to link to — every
 * audience resolver is scoped to `user: user.id`, so an admin following a link
 * to someone else's audience would be told it doesn't exist — so the detail
 * an admin would go looking for is rendered inline instead.
 */
function ActivationDetail({ activation }) {
  const { included, excluded } = extractVariablesFromAudienceFilters(
    activation.audience?.filters ?? null
  );

  const rows = [
    // Owner email is deliberately absent: the User column already carries it,
    // and repeating it here just makes the panel longer.
    ['Company', activation.user?.companyName],
    ['Audience ID', activation.audience?.id],
    [
      'Audience total records',
      activation.audience?.recordsAvailable != null
        ? Number(activation.audience.recordsAvailable).toLocaleString('en-US')
        : null,
    ],
    [
      'Delivered to',
      activation.deliveredKey
        ? `s3://${activation.deliveredBucket}/${activation.deliveredKey}`
        : null,
    ],
    ['Processing ID', activation.processingId],
  ].filter(([, value]) => value);

  return (
    <div className="grid grid-cols-1 gap-4 px-6 py-4 text-sm md:grid-cols-2">
      <dl className="space-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <dt className="text-gray-500">{label}:</dt>
            <dd className="break-all font-medium text-gray-800">{value}</dd>
          </div>
        ))}
      </dl>
      <div>
        <p className="mb-1 text-gray-500">Audience attributes</p>
        {included.length === 0 && excluded.length === 0 ? (
          <p className="text-gray-500">
            {activation.audience
              ? 'No attribute filters recorded.'
              : 'The audience for this activation no longer exists.'}
          </p>
        ) : (
          <p className="text-gray-800">
            {included.join(', ')}
            {excluded.length > 0 && (
              <span className="text-gray-500">
                {included.length > 0 ? ' · ' : ''}excluding{' '}
                {excluded.join(', ')}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Rows are the raw GraphQL shape, which this codebase does not generate types
 * for; `any` matches the sibling tables rather than inventing a local model
 * that would drift from the query.
 */
type ActivationRow = any;

type ActivationsTableProps = {
  activations: ActivationRow[];
};

/** The Clear Filters handle the page drives through its single table ref. */
export type ActivationsTableHandle = {
  setAllFilters: (filters: unknown[]) => void;
};

const ActivationsTable = forwardRef<
  ActivationsTableHandle,
  ActivationsTableProps
>(({ activations }, ref) => {
  const data = useMemo(() => activations, [activations]);

  const columns = useMemo(
    () => [
      {
        Header: 'Activation Date',
        id: 'createdAt',
        accessor: (row) => toEpoch(row.createdAt),
        Cell: ({ value }) =>
          formatDateCellCustom({
            value,
            ifEmpty: EMPTY,
            pattern: 'MMM d, yyyy h:mm a',
          }),
        disableFilters: true,
      },
      {
        Header: 'User',
        id: 'userEmail',
        accessor: (row) => row.user?.email || 'N/A',
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        Header: 'Audience',
        id: 'audienceName',
        accessor: activationAudienceName,
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        Header: 'Audience Size',
        id: 'audienceSize',
        accessor: (row) => row.audience?.recordsIncluded ?? null,
        Cell: ({ value }) => formatNumberWithCommas({ value, ifEmpty: EMPTY }),
        disableFilters: true,
      },
      {
        Header: 'Records Activated',
        id: 'recordsIncluded',
        accessor: (row) => row.recordsIncluded ?? null,
        Cell: ({ value }) => formatNumberWithCommas({ value, ifEmpty: EMPTY }),
        disableFilters: true,
      },
      {
        Header: 'Destination',
        id: 'destination',
        accessor: activationDestination,
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        // The LiveRamp variant — which of the five folders the file landed in.
        // This is the column the request was actually about.
        Header: 'Folder',
        id: 'destinationFolder',
        accessor: (row) => row.destinationPartner?.displayName || EMPTY,
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      // The delivered object key is deliberately not a column: it is long
      // enough to dominate the row, and it is a lookup value rather than
      // something to scan across rows. It lives in the drill-in below, and in
      // the CSV export.
      {
        Header: 'Status',
        id: 'processingStatus',
        accessor: (row) => row.processingStatus,
        Cell: StatusCell,
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        Header: 'Finished',
        id: 'finishedAt',
        accessor: (row) => toEpoch(row.finishedAt),
        Cell: ({ value }) =>
          formatDateCellCustom({
            value,
            ifEmpty: EMPTY,
            pattern: 'MMM d, yyyy h:mm a',
          }),
        disableFilters: true,
      },
      {
        Header: 'Details',
        id: 'details',
        Cell: ({ row }) => (
          <span
            className="cursor-pointer font-bold text-blue hover:text-primary"
            {...row.getToggleRowExpandedProps()}
          >
            {row.isExpanded ? 'Hide' : 'Show'}
          </span>
        ),
        disableSortBy: true,
        disableFilters: true,
      },
    ],
    []
  );

  const initialSortBy = useMemo(() => [{ id: 'createdAt', desc: true }], []);

  const tableInstance = useTable(
    { columns, data, initialState: { sortBy: initialSortBy } },
    useFilters,
    useSortBy,
    useExpanded
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setAllFilters,
    visibleColumns,
  } = tableInstance;

  // Same handle OrdersTable exposes, so the page's Clear Filters button works
  // against whichever table is currently mounted.
  useImperativeHandle(ref, () => ({
    setAllFilters,
  }));

  return (
    <div className="h-full w-full overflow-auto pb-14">
      <div className="mb-2 text-sm text-gray-600">
        Showing {rows.length} of {data.length} activations
      </div>
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border bg-gray-200 p-2 text-left hover:bg-gray-100"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {column.render('Header')}
                      {column.canSort &&
                        (column.isSorted ? (
                          <span className="pl-2">
                            {column.isSortedDesc ? (
                              <FaArrowDown />
                            ) : (
                              <FaArrowUp />
                            )}
                          </span>
                        ) : (
                          <FaSort className="ml-2 text-gray-400" size={12} />
                        ))}
                    </div>
                    <div className="flex items-center">
                      {column.canFilter ? column.render('Filter') : null}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            const { key, ...rowProps } = row.getRowProps();
            return (
              <Fragment key={key}>
                <tr {...rowProps}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className="border p-2">
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td colSpan={visibleColumns.length} className="border">
                      <ActivationDetail activation={row.original} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

ActivationsTable.displayName = 'ActivationsTable';

export default ActivationsTable;
